import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DatePicker } from '@material-ui/pickers'
import { Select, MenuItem } from '@material-ui/core'

import moment from 'config/moment'
import { snackbar } from 'components'
import { useApi } from 'hooks'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { ConfigUtils } from 'utils/config'

import { getStores } from '../StoresAndDateFilter/api'
import { GetStoresResponse, Store } from '../StoresAndDateFilter/types'
import GeneralMetrics, { GeneralMetricsDisplayData } from './GeneralMetrics/GeneralMetrics'
import { NewVsReturning } from './NewVsReturning/NewVsReturning'
import { AverageFrequency } from './AverageFrequency/AverageFrequency'
import { montserratLightStyle, montserratStyle } from '../styles'
import { useDashboardFilters } from '../contexts/DashboardFiltersContext'
import BenefitsUsed from '../LoyaltyEfficiencyTab/BenefitsUsed/BenefitsUsed'
import { StoresTopFive } from '../../Dashboard/Stores'
import { MetricCard } from '../components/MetricCard'
import { formatDashboardNumber } from '../utils/formatDashboardNumber'

const periodOptions = { 12: 'Últimos 30 días', 13: 'Últimos 7 días' }

const getPreviousMonths = () => {
  const months: { [key: number]: string } = {}
  const now = moment()

  // Get last 6 months including current month
  for (let i = 0; i < 6; i += 1) {
    const monthDate = now.clone().subtract(i, 'months')
    const monthIndex = monthDate.month() // 0-11
    // Use a unique key: monthsAgo * 100 + monthIndex to avoid conflicts
    // This way: 0 months ago = current month, 1 = last month, etc.
    const key = i * 100 + monthIndex
    const monthName = monthDate.format('MMMM YYYY')
    const capitalizedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1)
    months[key] = capitalizedMonthName
  }
  return months
}

const OverviewTab = () => {
  const {
    filters,
    handleStartPeriod1Change,
    handleEndPeriod1Change,
    handleStartPeriod2Change,
    handleEndPeriod2Change,
    handleComparaChange,
    handlePeriodSelect,
    handleSelectedStoresChange,
    handleApply,
    isActiveTab,
  } = useDashboardFilters()
  const {
    startPeriod1,
    endPeriod1,
    startPeriod2,
    endPeriod2,
    compara,
    applyTrigger,
    isLoading,
    selectedStoreIds,
  } = filters

  // Fetch stores
  const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []
  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    getStoresResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show('Error al cargar las tiendas'),
    },
  )

  const previousMonths = getPreviousMonths()

  // Get default period value (current month = 0 months ago)
  const getDefaultPeriodValue = () => {
    const now = moment()
    const currentMonthIndex = now.month()
    // Current month = 0 months ago, so key is 0 * 100 + monthIndex
    return 0 * 100 + currentMonthIndex
  }

  // Local state for month/period selector so we can reset it on manual date changes
  // Default to current month (0 months ago)
  const defaultPeriodValue = React.useMemo(() => getDefaultPeriodValue(), [])
  const [periodSelectValue, setPeriodSelectValue] = React.useState<number>(defaultPeriodValue)

  const onApply = () => {
    if (startPeriod1.isAfter(endPeriod1)) {
      snackbar.show('La fecha de inicio del período principal debe ser anterior a la fecha de fin')
      return
    }

    if (startPeriod2.isAfter(endPeriod2)) {
      snackbar.show(
        'La fecha de inicio del período de comparación debe ser anterior a la fecha de fin',
      )
      return
    }

    if (compara && endPeriod2.isAfter(startPeriod1)) {
      snackbar.show('El período de comparación debe ser anterior al período principal')
      return
    }

    handleApply()
  }

  // Only trigger API calls if this tab is active
  const shouldTrigger = isActiveTab('overview')

  // Ejecutar indicadores secuencialmente: uno termina -> arranca el siguiente
  const [step, setStep] = useState(0)
  const runRef = useRef(0)
  const [generalMetricsSnapshot, setGeneralMetricsSnapshot] = useState<{
    isLoading: boolean
    displayData: GeneralMetricsDisplayData
  } | null>(null)

  const hasDeliveryIntegration = ConfigUtils.integrations.tucan.enabled
  const handleGeneralMetricsSnapshot = useCallback(
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    (snapshot: { isLoading: boolean; displayData: GeneralMetricsDisplayData }) => {
      setGeneralMetricsSnapshot(snapshot)
    },
    [],
  )

  useEffect(() => {
    if (!shouldTrigger) return
    runRef.current = applyTrigger
    setStep(0)
  }, [applyTrigger, shouldTrigger])

  const areAllStoresSelected = React.useMemo(() => {
    if (!stores || stores.length === 0) return false
    if (!selectedStoreIds || selectedStoreIds.length === 0) return true // Empty = all stores

    const allStoreIds = stores.filter(store => !store.deletedAt).map(store => store.id)

    // Check if selectedStoreIds contains all store IDs and has the same length
    return (
      selectedStoreIds.length === allStoreIds.length &&
      allStoreIds.every(id => selectedStoreIds.includes(id)) &&
      selectedStoreIds.every(id => allStoreIds.includes(id)) // Also check reverse to ensure no extra IDs
    )
  }, [stores, selectedStoreIds])

  return (
    <div className="flex flex-col gap-8 p-6 bg-gray-50 min-h-screen">
      <div className="mb-4">
        <div className="flex flex-row items-end gap-8 w-full">
          <div className="flex-1 flex flex-row items-end gap-4">
            {/* Selector de tiendas */}
            <div style={{ width: '200px' }}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="stores-select"
                className="block text-sm text-gray-500 mb-1"
                style={{ ...montserratLightStyle }}
              >
                Locales
              </label>
              <Select
                id="stores-select"
                multiple
                value={selectedStoreIds ?? []}
                onChange={e => {
                  const value = e.target.value as (number | string)[]
                  if (Array.isArray(value) && value.includes('all')) {
                    handleSelectedStoresChange([])
                    return
                  }
                  if (Array.isArray(value) && value.length === 0) {
                    handleSelectedStoresChange([])
                    return
                  }
                  handleSelectedStoresChange(value as number[])
                }}
                fullWidth
                style={{ ...montserratStyle }}
                displayEmpty
                renderValue={selected => {
                  const count = Array.isArray(selected) ? selected.length : 0
                  const allStoreIds = stores
                    .filter(store => !store.deletedAt)
                    .map(store => store.id)
                  const hasAllStores =
                    count > 0 &&
                    count === allStoreIds.length &&
                    allStoreIds.every(id => (selected as number[]).includes(id))
                  if (count === 0 || hasAllStores) return 'Todas las tiendas'
                  if (count === 1) return '1 local'
                  return `${formatDashboardNumber(count)} locales`
                }}
              >
                <MenuItem value="all">Todas las tiendas</MenuItem>
                {stores
                  .filter(store => !store.deletedAt)
                  .map(store => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.name}
                    </MenuItem>
                  ))}
              </Select>
            </div>

            {/* Selector de período predefinido */}
            <div style={{ width: '180px' }}>
              <Select
                value={periodSelectValue}
                onChange={e => {
                  const value = e.target.value as number
                  setPeriodSelectValue(value)
                  if (value >= 0) {
                    handlePeriodSelect(value)
                  }
                }}
                fullWidth
                style={{ ...montserratStyle }}
              >
                <MenuItem value={-1}>Seleccionar período</MenuItem>
                {Object.entries(previousMonths).map(([monthIndex, monthName]) => (
                  <MenuItem key={monthIndex} value={parseInt(monthIndex, 10)}>
                    {monthName}
                  </MenuItem>
                ))}
                {Object.entries(periodOptions).map(([key, label]) => (
                  <MenuItem key={key} value={parseInt(key, 10)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* Periodo 1 - Desde */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="start-period1"
                className="block text-sm text-gray-500 mb-1"
                style={{ ...montserratLightStyle }}
              >
                Desde
              </label>
              <DatePicker
                value={startPeriod1}
                onChange={date => {
                  handleStartPeriod1Change(date)
                  setPeriodSelectValue(-1)
                }}
                format="DD/MM/YYYY"
                InputProps={{
                  id: 'start-period1',
                  disableUnderline: false,
                  style: { fontSize: 18, color: '#6B7280', paddingLeft: 8, ...montserratStyle },
                }}
                style={{ width: '150px', borderRadius: 8 }}
                maxDate={endPeriod1}
              />
            </div>
            {/* Periodo 1 - Hasta */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="end-period1"
                className="block text-sm text-gray-500 mb-1"
                style={{ ...montserratLightStyle }}
              >
                Hasta
              </label>
              <DatePicker
                value={endPeriod1}
                onChange={date => {
                  handleEndPeriod1Change(date)
                  setPeriodSelectValue(-1)
                }}
                format="DD/MM/YYYY"
                InputProps={{
                  id: 'end-period1',
                  disableUnderline: false,
                  style: { fontSize: 18, color: '#6B7280', paddingLeft: 8, ...montserratStyle },
                }}
                style={{ width: '150px', borderRadius: 8 }}
                minDate={startPeriod1}
                maxDate={moment()}
              />
            </div>

            <span className="text-s text-gray-500 mb-2" style={{ ...montserratLightStyle }}>
              Comparar con
            </span>

            {/* Periodo 2 - Desde */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="start-period2"
                className="block text-sm text-gray-500 mb-1"
                style={{ ...montserratLightStyle }}
              >
                Desde
              </label>
              <DatePicker
                value={startPeriod2}
                onChange={date => {
                  handleStartPeriod2Change(date)
                  setPeriodSelectValue(-1)
                }}
                format="DD/MM/YYYY"
                InputProps={{
                  id: 'start-period2',
                  disableUnderline: false,
                  style: { fontSize: 18, color: '#6B7280', paddingLeft: 8, ...montserratStyle },
                }}
                style={{ width: '150px', borderRadius: 8 }}
                disabled={!compara}
                maxDate={moment.min([endPeriod2, startPeriod1.clone().subtract(1, 'day')])}
              />
            </div>
            {/* Periodo 2 - Hasta */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="end-period2"
                className="block text-sm text-gray-500 mb-1"
                style={{ ...montserratLightStyle }}
              >
                Hasta
              </label>
              <DatePicker
                value={endPeriod2}
                onChange={date => {
                  handleEndPeriod2Change(date)
                  setPeriodSelectValue(-1)
                }}
                format="DD/MM/YYYY"
                InputProps={{
                  id: 'end-period2',
                  disableUnderline: false,
                  style: { fontSize: 18, color: '#6B7280', paddingLeft: 8, ...montserratStyle },
                }}
                style={{ width: '150px', borderRadius: 8 }}
                disabled={!compara}
                minDate={startPeriod2}
                maxDate={moment.min([startPeriod1.clone().subtract(1, 'day'), moment()])}
              />
            </div>
            {/* Checkbox y botón de carga CSV */}
            <div className="flex items-center ml-3 mr-4">
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    checked={compara}
                    onChange={handleComparaChange}
                    color="primary"
                  />
                }
                label="Comparar"
                style={{ ...montserratLightStyle }}
              />
            </div>
          </div>
          <button
            type="button"
            className={`rounded px-6 py-2 font-semibold ml-auto ${
              isLoading
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-[#26342E] text-white hover:bg-[#1a2a24]'
            }`}
            onClick={onApply}
            disabled={isLoading}
            style={{ ...montserratLightStyle }}
          >
            {isLoading ? 'Cargando...' : 'Aplicar'}
          </button>
        </div>
      </div>
      <GeneralMetrics
        period1={{ startDate: startPeriod1, endDate: endPeriod1 }}
        period2={{ startDate: startPeriod2, endDate: endPeriod2 }}
        compara={compara}
        trigger={shouldTrigger && step === 0 ? applyTrigger : 0}
        storeIds={selectedStoreIds}
        hidePhysicalAndDelivery={!hasDeliveryIntegration}
        onSnapshot={handleGeneralMetricsSnapshot}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 0 ? 1 : prev))
        }}
      />
      <AverageFrequency
        period1={{ startDate: startPeriod1, endDate: endPeriod1 }}
        period2={{ startDate: startPeriod2, endDate: endPeriod2 }}
        compara={compara}
        trigger={shouldTrigger && step === 1 ? applyTrigger : 0}
        storeIds={selectedStoreIds}
        extraCard={
          !hasDeliveryIntegration && generalMetricsSnapshot ? (
            <MetricCard
              title="Registros físicos"
              value={generalMetricsSnapshot.displayData.registrosFisicos.value}
              prev={generalMetricsSnapshot.displayData.registrosFisicos.prev}
              diff={generalMetricsSnapshot.displayData.registrosFisicos.diff}
              isPositive={generalMetricsSnapshot.displayData.registrosFisicos.isPositive}
              showComparison={compara}
              comparisonMonth={startPeriod2.format('DD/MM')}
              comparisonYear={endPeriod2.format('DD/MM/YYYY')}
              isLoading={generalMetricsSnapshot.isLoading}
            />
          ) : null
        }
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 1 ? 2 : prev))
        }}
      />
      <NewVsReturning
        period1={{ startDate: startPeriod1, endDate: endPeriod1 }}
        trigger={shouldTrigger && step === 2 ? applyTrigger : 0}
        storeIds={selectedStoreIds}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 2 ? 3 : prev))
        }}
      />

      {/* <Top3Redemptions
        period1={{ startDate: startPeriod1, endDate: endPeriod1 }}
        trigger={shouldTrigger ? applyTrigger : 0}
        storeIds={selectedStoreIds}
      /> 

      <RewardsTopFive
        startDate={startPeriod1}
        endDate={endPeriod1}
        trigger={shouldTrigger ? applyTrigger : 0}
        storeIds={selectedStoreIds}
      /> */}
      <StoresTopFive
        startDate={startPeriod1}
        endDate={endPeriod1}
        storeIds={selectedStoreIds}
        trigger={shouldTrigger && step === 3 ? applyTrigger : 0}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 3 ? 4 : prev))
        }}
      />
      <BenefitsUsed
        period={{ month: startPeriod1.month(), year: startPeriod1.year() }}
        startDate={startPeriod1}
        endDate={endPeriod1}
        storeIds={areAllStoresSelected ? undefined : selectedStoreIds}
        trigger={shouldTrigger && step === 4 ? applyTrigger : 0}
        compara={compara}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 4 ? 5 : prev))
        }}
      />
    </div>
  )
}

export default OverviewTab
